/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
import React from 'react'
import { FormattedMessage } from 'react-intl'

import { EuiButtonIcon, EuiPopover, EuiText } from '@elastic/eui'

import type { AnyTopologyElement } from '@modules/ui-types'
import { useToggle } from '@modules/utils/hooks/useToggle'

import DocLink from '@/components/DocLink'
import { isAutoscaleableTier } from '@/lib/stackDeployments/selectors/autoscaling'

type Props = {
  topologyElement: AnyTopologyElement
}

const AutoscalingInfo = ({ topologyElement }: Props) => {
  const [isPopoverOpen, togglePopover, setPopoverOpen] = useToggle()

  if (!isAutoscaleableTier({ topologyElement, version: undefined })) {
    return null
  }

  return (
    <EuiPopover
      data-test-subj='configureTemplate.autoscaling.popover'
      ownFocus={false}
      button={
        <EuiButtonIcon
          iconType='iInCircle'
          size='s'
          color='text'
          aria-label='Info'
          onClick={togglePopover}
        />
      }
      isOpen={isPopoverOpen}
      closePopover={() => setPopoverOpen(false)}
    >
      <EuiText style={{ width: 350 }}>
        <FormattedMessage
          id='deploymentTemplateInfrastructure-templateTopologyElement-autoscalingInfo'
          defaultMessage='The default size of a data tier. Users can {docLink} when they create or edit a deployment.'
          values={{
            docLink: (
              <DocLink link='autoscalingUpdateSettingsLink'>
                <FormattedMessage
                  id='configure-template.autoscaling.popover.doclink'
                  defaultMessage='modify this value'
                />
              </DocLink>
            ),
          }}
        />
      </EuiText>
    </EuiPopover>
  )
}

export default AutoscalingInfo
